import {
  Box,
  Grid,
  Link,
  Stack,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Tooltip,
} from "@mui/material";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { FeedGrid } from "../components/CommercialPictureAnalysisDetailFeedGrid";
import { KeyboardArrowLeft } from "@mui/icons-material";
import React, { useState } from "react";
import { useApiClient } from "@/hooks/useApiClient";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import {
  isRequestCompleted,
  statusToLabel,
} from "@/features/commercialPictureAnalysis/utils";
import { type RequestDetailAndCorporations } from "@/features/commercialPictureAnalysis/types";
import CircularProgress from "@mui/material/CircularProgress";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { format } from "date-fns";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { CommercialPictureAnalysisEditModal } from "@/features/commercialPictureAnalysis/components/CommercialPictureAnalysisEditModal";
import { toast } from "react-toastify";
import { ContactUsAlert } from "@/components/ContactUsAlert";
import * as Sentry from "@sentry/react";
import { z } from "zod";

const commercialPictureAnalysisDetailContractSchema = z
  .object({
    isOtherUserResource: z.boolean(),
  })
  .passthrough();

/**
 * 商業登記解析結果詳細APIのレスポンスが契約を満たしているか検証する。
 * @param payload APIレスポンスのペイロード
 * @param endpoint 呼び出し元のAPIエンドポイント
 * @throws {Error} API契約違反がある場合に例外を送出する
 */
const validateCommercialPictureAnalysisDetailContract = (
  payload: unknown,
  endpoint: string
): void => {
  const parsed =
    commercialPictureAnalysisDetailContractSchema.safeParse(payload);
  if (parsed.success) return;

  const error = new Error(`API contract violation at ${endpoint}`);
  Sentry.captureException(error, {
    extra: {
      endpoint,
      issues: parsed.error.issues,
    },
  });
  throw error;
};

export const CommercialPictureAnalysisDetail: React.FC = () => {
  const { apiClient } = useApiClient();
  const { id } = useParams();
  const identificationNameDisabledMessage =
    "他ユーザーが設定した識別名は編集できません";
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const fetcher = async (
    url: string
  ): Promise<RequestDetailAndCorporations> => {
    const { data } = await apiClient.get<RequestDetailAndCorporations>(url);
    validateCommercialPictureAnalysisDetailContract(data, url);
    return data;
  };

  const { data, error, mutate } = useSWR<RequestDetailAndCorporations, Error>(
    id ? `/commercial-picture-analyze/${id}` : null,
    fetcher,
    {
      revalidateOnFocus: true,
      refreshInterval: 60000,
      dedupingInterval: 30000,
    }
  );

  if (error != null && data === undefined) {
    return (
      <PagePaper>
        <Stack alignItems="center">
          <Typography color="error" sx={{ my: "20px" }}>
            データの取得に失敗しました。
          </Typography>
        </Stack>
      </PagePaper>
    );
  }

  if (data == null) {
    return <CircularProgress />;
  }

  const datetimeFormatter = (datetime: string): string => {
    return format(new Date(datetime), "yyyy/MM/dd HH:mm");
  };

  // 識別名を更新後、すぐにデータの再フェッチを行う
  const handleRefreshAfterEdit = (): void => {
    mutate().catch(() => {
      toast.error("データの再取得に失敗しました。");
    });
  };

  return (
    <PagePaper>
      <form>
        <Grid container sx={{ mb: 2 }} rowSpacing={1}>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                {/* ページタイトル */}
                <Box sx={{ display: "flex", mb: 3 }}>
                  <LibraryBooksIcon sx={{ mr: 1 }} fontSize="large" />
                  <PageTitle>商業登記データ化詳細</PageTitle>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <ContactUsAlert />
              </Grid>
              <Grid item xs={12}>
                <Table size="small" sx={{ mb: 2, ml: 1 }}>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        ステータス
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {statusToLabel(data?.status)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        件数
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {formatNumberWithComma(data?.details.length)}件（成功：
                        {formatNumberWithComma(
                          data?.details.filter(
                            (detail) => detail.status === "succeeded"
                          ).length
                        )}
                        件、 失敗：
                        {formatNumberWithComma(
                          data?.details.filter(
                            (detail) => detail.status === "failed"
                          ).length
                        )}
                        件）
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        識別番号
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {data?.taskId}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        識別名
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {data?.identificationName}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        依頼日時
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {datetimeFormatter(data?.createdAt)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12} sx={{ textAlign: "left" }}>
                <Tooltip
                  title={
                    data?.isOtherUserResource
                      ? identificationNameDisabledMessage
                      : ""
                  }
                  placement="top"
                  arrow
                  disableHoverListener={!data?.isOtherUserResource}
                >
                  <span>
                    <Button
                      variant="outlined"
                      component={Link}
                      onClick={() => {
                        setOpenEditDialog(true);
                      }}
                      disabled={data?.isOtherUserResource}
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      識別名編集
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <SectionPaper>
          <FeedGrid
            row={data?.details}
            requestId={data?.id}
            disabledAction={
              !isRequestCompleted(data?.status) || data?.succeededCount === 0
            }
            onDownloadComplete={() => {
              mutate().catch(() => {
                toast.error("データの再取得に失敗しました。");
              });
            }}
          />
          <Box sx={{ mb: 6 }} />
          <Stack
            direction={"row"}
            spacing={4}
            justifyContent={"center"}
            mb={2}
          ></Stack>
        </SectionPaper>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            href="/commercialpictureanalysis/list"
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>一覧に戻る</Typography>
            </Box>
          </Link>
        </Box>
      </form>
      {openEditDialog && !data?.isOtherUserResource && (
        <CommercialPictureAnalysisEditModal
          open={openEditDialog}
          setOpen={setOpenEditDialog}
          handleClose={handleRefreshAfterEdit}
          analyzeRequestId={Number(id)}
          data={data}
        />
      )}
    </PagePaper>
  );
};
